import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Pulse } from "@shared/schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { MessageSquare, Globe, Send, Loader2, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { getUserColor } from '@/lib/user-color';

interface PulseShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pulse: Pulse;
  projectId: string;
  projectName: string;
}

interface TeamMember {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  role: string;
  shortCode?: string | null;
  colorCode?: string | null;
}

interface ChatChannel {
  id: string;
  type: string;
  name: string | null;
  projectId: string | null;
}

type ShareTarget = 'project' | 'global' | `direct-${number}`;

export function PulseShareDialog({
  open,
  onOpenChange,
  pulse,
  projectId,
  projectName,
}: PulseShareDialogProps) {
  const { toast } = useToast();
  const [shareTarget, setShareTarget] = useState<ShareTarget>('project');
  const [optionalMessage, setOptionalMessage] = useState('');

  // Get users with project access (admins + lead artist + assigned artists)
  const { data: projectTeam = [], isLoading: isLoadingTeam } = useQuery<TeamMember[]>({
    queryKey: ['/api/projects', projectId, 'team-access'],
    enabled: open && !!projectId,
  });

  // Get project channel (only when projectId is available)
  const { data: projectChannel } = useQuery<ChatChannel>({
    queryKey: ['/api/chat/channels/project', projectId],
    enabled: open && shareTarget === 'project' && !!projectId,
  });

  // Get global channel
  const { data: globalChannel } = useQuery<ChatChannel>({
    queryKey: ['/api/chat/channels/global'],
    enabled: open && shareTarget === 'global',
  });

  // Get or create direct channel for selected user
  const getDirectChannelMutation = useMutation({
    mutationFn: async (userId: number) => {
      const response = await apiRequest('POST', `/api/chat/channels/direct/${userId}`, {});
      return response.json();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to get/create direct channel",
        variant: "destructive",
      });
    },
  });

  // Share pulse mutation
  const sharePulseMutation = useMutation({
    mutationFn: async (data: { channelId: string; pulseId: number; projectId: string; projectName: string; optionalMessage?: string }) => {
      const response = await apiRequest('POST', '/api/chat/share-pulse', data);
      return response.json();
    },
    onSuccess: () => {
      const targetName = shareTarget === 'project' 
        ? 'project chat' 
        : shareTarget === 'global' 
          ? 'global chat'
          : 'direct message';
      toast({
        title: "Pulse shared",
        description: `Pulse has been shared to ${targetName}`,
      });
      onOpenChange(false);
      setOptionalMessage('');
      setShareTarget('project');
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to share pulse. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleShare = async () => {
    let channelId: string | undefined;

    if (shareTarget === 'project') {
      channelId = projectChannel?.id;
    } else if (shareTarget === 'global') {
      channelId = globalChannel?.id;
    } else if (shareTarget.startsWith('direct-')) {
      const userId = parseInt(shareTarget.replace('direct-', ''));
      try {
        const response = await getDirectChannelMutation.mutateAsync(userId);
        // API returns {channel: {id: ...}} wrapper
        channelId = response.channel?.id || response.id;
      } catch {
        return;
      }
    }

    if (!channelId) {
      toast({
        title: "Error",
        description: "Could not find target channel",
        variant: "destructive",
      });
      return;
    }

    if (!projectId) {
      toast({
        title: "Error",
        description: "Project ID is required",
        variant: "destructive",
      });
      return;
    }

    sharePulseMutation.mutate({
      channelId,
      pulseId: pulse.id,
      projectId,
      projectName,
      optionalMessage: optionalMessage.trim() || undefined,
    });
  };

  const isLoading = sharePulseMutation.isPending || getDirectChannelMutation.isPending;

  // Get sequence info for display
  const sequenceDisplay = (pulse as any).sequences?.[0]?.sequenceNumber || '';

  // Helper to get user display name
  const getUserDisplayName = (member: TeamMember) => {
    if (member.firstName && member.lastName) {
      return `${member.firstName} ${member.lastName}`;
    }
    return member.email.split('@')[0];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-gray-800 border-gray-600">
        <DialogHeader>
          <DialogTitle className="text-white">Share Pulse</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Pulse Preview */}
          <div className="border border-white/10 bg-white/5 rounded p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1 h-4 bg-primary rounded-full" />
              <span className="font-bold text-sm text-white">{projectId}: {projectName}</span>
            </div>
            <div className="text-xs text-muted-foreground line-clamp-2">
              {sequenceDisplay && <span className="text-primary mr-1">{sequenceDisplay}</span>}
              {pulse.pulseDescription}
            </div>
          </div>

          {/* Share Target Selection - Flat List */}
          <div className="space-y-3">
            <Label className="text-gray-300">Share to:</Label>
            <RadioGroup
              value={shareTarget}
              onValueChange={(value) => setShareTarget(value as ShareTarget)}
              className="space-y-1"
            >
              {/* Project Chat */}
              <div 
                className={cn(
                  "flex items-center space-x-3 p-2 rounded cursor-pointer transition-colors",
                  shareTarget === 'project' ? "bg-primary/20 border border-primary/40" : "hover:bg-white/5"
                )}
                onClick={() => setShareTarget('project')}
              >
                <RadioGroupItem value="project" id="project" />
                <Label htmlFor="project" className="flex items-center gap-2 text-gray-300 cursor-pointer flex-1">
                  <MessageSquare className="w-4 h-4" />
                  Project Chat
                </Label>
              </div>

              {/* Direct Message Users */}
              {isLoadingTeam ? (
                <div className="flex items-center justify-center py-2 text-gray-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Loading team...
                </div>
              ) : projectTeam.length > 0 ? (
                projectTeam.map((member) => (
                  <div 
                    key={member.id}
                    className={cn(
                      "flex items-center space-x-3 p-2 rounded cursor-pointer transition-colors",
                      shareTarget === `direct-${member.id}` ? "bg-primary/20 border border-primary/40" : "hover:bg-white/5"
                    )}
                    onClick={() => setShareTarget(`direct-${member.id}`)}
                  >
                    <RadioGroupItem value={`direct-${member.id}`} id={`direct-${member.id}`} />
                    <Label 
                      htmlFor={`direct-${member.id}`} 
                      className="flex items-center gap-2 text-gray-300 cursor-pointer flex-1"
                    >
                      <div 
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-medium"
                        style={{ backgroundColor: getUserColor(member.colorCode) }}
                      >
                        {member.shortCode?.[0]?.toUpperCase() || <User className="w-3 h-3" />}
                      </div>
                      <span className="flex-1 truncate">{getUserDisplayName(member)}</span>
                      {member.shortCode && (
                        <span className="text-xs text-muted-foreground">@{member.shortCode}</span>
                      )}
                    </Label>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 text-xs py-1 px-2">
                  No team members with project access
                </div>
              )}

              {/* Global Chat */}
              <div 
                className={cn(
                  "flex items-center space-x-3 p-2 rounded cursor-pointer transition-colors",
                  shareTarget === 'global' ? "bg-primary/20 border border-primary/40" : "hover:bg-white/5"
                )}
                onClick={() => setShareTarget('global')}
              >
                <RadioGroupItem value="global" id="global" />
                <Label htmlFor="global" className="flex items-center gap-2 text-gray-300 cursor-pointer flex-1">
                  <Globe className="w-4 h-4" />
                  Global Chat
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Optional Message */}
          <div className="space-y-2">
            <Label className="text-gray-300">Message (optional):</Label>
            <Textarea
              placeholder="Add a message..."
              value={optionalMessage}
              onChange={(e) => setOptionalMessage(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white resize-none"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleShare}
            disabled={isLoading}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sharing...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Share
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
